import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload, IconEdit, IconTrash } from '../components/icons';
import { useAuth } from '../auth/useAuth';
import { labelFor, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';

const SCHEDULE_STATUS_BADGE: Record<string, string> = {
  draft: 'badge-scheduled',
  submitted: 'badge-neutral',
  receipted: 'badge-active',
};

interface EmploymentRow {
  id: number;
  start_date: string;
  end_date: string | null;
  member: { id: number; user: { id: number; first_name: string; last_name: string; email: string } | null } | null;
}

interface EmployerDocument {
  id: number;
  original_name: string;
  notes: string | null;
}

interface EmployerRecord {
  id: number;
  name: string;
  registration_number: string;
  tin: string | null;
  address: string | null;
  postal_address: string | null;
  contact_person: string | null;
  phone: string | null;
  email: string | null;
  industry_sector: string | null;
  bank_name: string | null;
  bank_account: string | null;
  contribution_frequency: string;
  status: string;
  notes: string | null;
  employments: EmploymentRow[];
  documents: EmployerDocument[];
  latest_contribution_schedule: {
    id: number;
    period_start: string;
    period_end: string;
    status: 'draft' | 'submitted' | 'receipted';
  } | null;
}

export function EmployerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lists } = useConfig();
  const { can } = useAuth();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [employer, setEmployer] = useState<EmployerRecord | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const canUpdate = can('memberships.update');
  const canArchive = can('memberships.archive');

  const load = () => api.get<EmployerRecord>(`/employers/${id}`).then((res) => setEmployer(res.data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const remove = async () => {
    if (!employer) return;
    if (!(await confirmDelete(`Delete ${employer.name}?`))) return;
    try {
      await api.delete(`/employers/${id}`);
      navigate('/memberships/employers');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const pickFile = () => fileInputRef.current?.click();

  const onFileSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const data = new FormData();
    data.append('file', file);
    try {
      await api.post(`/employers/${id}/documents`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Upload failed.');
    }
  };

  const removeDocument = async (documentId: number) => {
    if (!(await confirmDelete('Remove this document?'))) return;
    await api.delete(`/employers/${id}/documents/${documentId}`);
    load();
  };

  const download = (doc: EmployerDocument) =>
    downloadFile(api, `/employers/${id}/documents/${doc.id}/download`, doc.original_name);

  if (!employer) return null;

  const current = employer.employments.filter((e) => e.end_date === null);
  const past = employer.employments.filter((e) => e.end_date !== null);

  const memberName = (row: EmploymentRow) =>
    row.member?.user ? `${row.member.user.first_name} ${row.member.user.last_name}` : '—';

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/memberships/employers')} />
          {canUpdate && <IconButton icon={IconEdit} label="Edit" to={`/memberships/employers/${id}/edit`} />}
          {canArchive && <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={remove} />}
        </div>
      </header>

      <div className="card">
        <div className="detail-summary">
          <div className="detail-summary-title">{employer.name}</div>
          <div className="detail-summary-service">{employer.registration_number}</div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">TIN</span>
            <span className={`detail-item-value${employer.tin ? '' : ' empty'}`}>{employer.tin || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Industry Sector</span>
            <span className={`detail-item-value${employer.industry_sector ? '' : ' empty'}`}>
              {employer.industry_sector ? labelFor(lists, 'industry_sector', employer.industry_sector) : 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Contact Person</span>
            <span className={`detail-item-value${employer.contact_person ? '' : ' empty'}`}>
              {employer.contact_person || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Phone</span>
            <span className={`detail-item-value${employer.phone ? '' : ' empty'}`}>{employer.phone || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Email</span>
            <span className={`detail-item-value${employer.email ? '' : ' empty'}`}>{employer.email || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Address</span>
            <span className={`detail-item-value${employer.address ? '' : ' empty'}`}>{employer.address || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Postal Address</span>
            <span className={`detail-item-value${employer.postal_address ? '' : ' empty'}`}>
              {employer.postal_address || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Bank</span>
            <span className={`detail-item-value${employer.bank_name ? '' : ' empty'}`}>
              {employer.bank_name ? `${employer.bank_name} — ${employer.bank_account || 'Not set'}` : 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Contribution Frequency</span>
            <span className="detail-item-value">
              {employer.contribution_frequency.charAt(0).toUpperCase() + employer.contribution_frequency.slice(1)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Status</span>
            <span className="detail-item-value">{employer.status.charAt(0).toUpperCase() + employer.status.slice(1)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Latest Contribution Schedule</span>
            <span className={`detail-item-value${employer.latest_contribution_schedule ? '' : ' empty'}`}>
              {employer.latest_contribution_schedule ? (
                <>
                  <Link to={`/memberships/contribution-schedules/${employer.latest_contribution_schedule.id}`}>
                    {employer.latest_contribution_schedule.period_start} – {employer.latest_contribution_schedule.period_end}
                  </Link>{' '}
                  <span className={`badge ${SCHEDULE_STATUS_BADGE[employer.latest_contribution_schedule.status]}`}>
                    {employer.latest_contribution_schedule.status}
                  </span>
                </>
              ) : (
                'None yet'
              )}
            </span>
          </div>
        </div>

        {employer.notes && (
          <div className="detail-item detail-item-full">
            <span className="detail-item-label">Notes</span>
            <p className="detail-notes">{employer.notes}</p>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Current Employees</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Since</th>
              </tr>
            </thead>
            <tbody>
              {current.map((row) => (
                <tr key={row.id}>
                  <td>{memberName(row)}</td>
                  <td className="muted">{row.member?.user?.email}</td>
                  <td className="muted">{row.start_date}</td>
                </tr>
              ))}
              {current.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted center">
                    No current employees.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>Past Employees</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Start</th>
                <th>End</th>
              </tr>
            </thead>
            <tbody>
              {past.map((row) => (
                <tr key={row.id}>
                  <td>{memberName(row)}</td>
                  <td className="muted">{row.member?.user?.email}</td>
                  <td className="muted">{row.start_date}</td>
                  <td className="muted">{row.end_date}</td>
                </tr>
              ))}
              {past.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted center">
                    No past employees.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>Documents</h2>
        <ul className="simple-list">
          {employer.documents.map((doc) => (
            <li key={doc.id}>
              <IconButton icon={IconDownload} label={doc.original_name} onClick={() => download(doc)} />
              {doc.original_name}
              {canUpdate && (
                <button type="button" className="btn btn-danger-ghost" onClick={() => removeDocument(doc.id)}>
                  Remove
                </button>
              )}
            </li>
          ))}
          {employer.documents.length === 0 && <li className="muted">No documents attached yet.</li>}
        </ul>
        {canUpdate && (
          <>
            <button type="button" className="btn btn-ghost" onClick={pickFile}>
              + Attach document
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/png,image/jpeg,.doc,.docx"
              onChange={onFileSelected}
              hidden
            />
          </>
        )}
      </div>
    </>
  );
}
